import { useCallback } from 'react';
import { graphql } from 'react-relay';

import { useReadFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useReadFragmentInterop.ts';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import useUpdateLastEditedTimestampVersionCacheLastEditedDraftVersionWriteFragment, {
	type useUpdateLastEditedTimestampVersionCacheLastEditedDraftVersionWriteFragment$data,
} from './__generated__/useUpdateLastEditedTimestampVersionCacheLastEditedDraftVersionWriteFragment.graphql.ts';
import useUpdateLastEditedTimestampVersionCacheLastEditedVersionWriteFragment, {
	type useUpdateLastEditedTimestampVersionCacheLastEditedVersionWriteFragment$data,
} from './__generated__/useUpdateLastEditedTimestampVersionCacheLastEditedVersionWriteFragment.graphql.ts';
import { usePageContentId } from '@confluence/page-context';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';

// if isOnEditRoute, updates the draft timestamp cache for edit page, edit blog, and drafts if you are currently editing that piece of content (i.e. change in editor content or rename title from quick actions while on editor of that page)
// otherwise, update published version history cache for the given content as all content types use published version history except for edit page, edit blog, & drafts
export const useUpdateLastEditedTimestampVersionCache = (): (({
	contentId,
}: {
	contentId: string;
}) => void) => {
	const readLastEditedDraftVersionFragmentFragmentInterop = useReadFragmentInterop<any>({
		fragmentSpec: graphql`
			fragment useUpdateLastEditedTimestampVersionCacheLastEditedDraftVersionReadFragment on Version {
				by {
					displayName
				}
				when
			}
		`,
	});
	const readLastEditedVersionFragmentFragmentInterop = useReadFragmentInterop<any>({
		fragmentSpec: graphql`
			fragment useUpdateLastEditedTimestampVersionCacheLastEditedVersionReadFragment on Version {
				by {
					displayName
				}
				when
			}
		`,
	});
	const writeLastEditedDraftVersionFragmentFragmentInterop =
		useWriteFragmentInterop<useUpdateLastEditedTimestampVersionCacheLastEditedDraftVersionWriteFragment$data>(
			{
				fragmentSpec: graphql`
					fragment useUpdateLastEditedTimestampVersionCacheLastEditedDraftVersionWriteFragment on Version {
						by {
							displayName
						}
						when
					}
				`,
				fragmentArtifact:
					useUpdateLastEditedTimestampVersionCacheLastEditedDraftVersionWriteFragment,
			},
		);
	const writeLastEditedVersionFragmentFragmentInterop =
		useWriteFragmentInterop<useUpdateLastEditedTimestampVersionCacheLastEditedVersionWriteFragment$data>(
			{
				fragmentSpec: graphql`
					fragment useUpdateLastEditedTimestampVersionCacheLastEditedVersionWriteFragment on Version {
						by {
							displayName
						}
						when
					}
				`,
				fragmentArtifact: useUpdateLastEditedTimestampVersionCacheLastEditedVersionWriteFragment,
			},
		);
	const { displayName } = useSessionData();
	const [currentPageContentId] = usePageContentId();
	const isOnEditRoute = useIsEditorPage();

	const updateTimestampCache = ({ contentId }: { contentId: string }) => {
		const newWhen = new Date().toISOString();

		const isEditingThisContent = isOnEditRoute && contentId === currentPageContentId;
		const id = `$Content:${contentId}.${isEditingThisContent ? 'draftVersion' : 'version'}`;

		let prevTypename = 'KnownUser';

		try {
			const readFragmentInterop = isEditingThisContent
				? readLastEditedDraftVersionFragmentFragmentInterop
				: readLastEditedVersionFragmentFragmentInterop;

			const prevCache = readFragmentInterop({ id });
			if (prevCache?.by?.__typename) {
				prevTypename = prevCache.by.__typename;
			}
		} catch {
			// silently fallback to default typename "KnownUser"
		}

		try {
			const writeFragmentInterop = isEditingThisContent
				? writeLastEditedDraftVersionFragmentFragmentInterop
				: writeLastEditedVersionFragmentFragmentInterop;

			writeFragmentInterop({
				fragmentId: id,
				data: {
					by: {
						displayName,
						__typename: prevTypename,
					},
					when: newWhen,
					__typename: 'Version',
				},
			});
		} catch {
			// silently fail write to cache
		}
	};

	const updateTimestampCacheWithCallback = useCallback(updateTimestampCache, [
		currentPageContentId,
		isOnEditRoute,
		displayName,
		readLastEditedDraftVersionFragmentFragmentInterop,
		readLastEditedVersionFragmentFragmentInterop,
		writeLastEditedDraftVersionFragmentFragmentInterop,
		writeLastEditedVersionFragmentFragmentInterop,
	]);

	return updateTimestampCacheWithCallback;
};
